import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { isEqual } from 'lodash';
import displayName from 'component-lib/displayName';

import styles from './Checkboxes.scss';

function determineColumnCount(children, idealRows, maxColumns) {
    return Math.min(Math.ceil(children.length / idealRows), maxColumns);
}

function groupChildren(columns, children) {
    return children.reduce((current, next, index) => {
        const column = index % columns;

        if (current.length > column) {
            current[column].push(next);
        } else {
            current.push([next]);
        }

        return current;
    }, []);
}

export default class CascadingCheckboxContainer extends Component {
    static displayName = displayName('Forms/Checkboxes/CascadingCheckboxContainer');

    static propTypes = {
        children: PropTypes.node.isRequired,
        idealRows: PropTypes.number.isRequired,
        maxColumns: PropTypes.number.isRequired,
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const { children, idealRows, maxColumns } = nextProps;
        const numColumns = determineColumnCount(children, idealRows, maxColumns);
        const hasNewChildren = !isEqual(prevState.cachedChildren, children);

        // We only care about re-grouping/rendering if we have new children or
        // our column layout changed (i.e. with 6 children:
        //     3 max columns & 3 ideal rows => 2 max columns & 4 ideal rows
        //     wouldn't trigger an update since they both result in 2 columns
        // ).
        if (hasNewChildren || numColumns !== prevState.columns) {
            return {
                columns: numColumns,
                table: groupChildren(numColumns, nextProps.children),
                cachedChildren: children,
            };
        }

        return {
            cachedChildren: nextProps.children,
        };
    }

    constructor(props) {
        super(props);

        // Store the grouping in the state so we don't do the re-grouping
        // every render call.
        const { children, idealRows, maxColumns } = this.props;
        const numColumns = determineColumnCount(children, idealRows, maxColumns);
        this.state = {
            columns: numColumns,
            table: groupChildren(numColumns, this.props.children),
        };
    }

    render() {
        return (
            <span className={styles['checkbox-cascading']}>
                {this.state.table.map((column, index) => (
                    /* eslint-disable react/no-array-index-key */
                    <div key={index}>
                        {column}
                    </div>
                    /* eslint-enable react/no-array-index-key */
                ))}
            </span>
        );
    }
}
